import {api, getFileStream} from '@/api/client';
import { config } from '@/config/env';
import type {
    AIResponseEvent,
    AIShareEvent,
    ConversationParams,
    GetAIResponseOptions,
    GetGoodFeedbackParams
} from "@/types";
import useCounterStore from "@/store";
import { envStorage } from '@/utils/envCache';

// 定义事件接口
export interface Event {
    event: string;
    data: Record<string, unknown>;
}

// 定义AI响应选项接口

/**
 * 获取AI问答数据
 *
 * @param question - 用户提出的问题
 * @param options - 请求选项
 */
export async function getAIResponse(
    question: string,
    options: GetAIResponseOptions = {}
): Promise<Event[]> {
    // 检查是否有访问令牌
    const accessToken = envStorage.getItem('accessToken');
    if (!accessToken) {
        return [{
            event: 'ERROR',
            data: {
                message: 'No access token found',
                time: Date.now(),
                errorType: 'AuthenticationError',
            }
        }];
    }

    // 合并选项和环境配置
    const useStream = options.stream ?? config.featureFlags.streaming;

    // 真实API请求
    try {
        if (useStream) {
            const response = await api.stream('/ai/ask', {
                data: { question }
            });
            return parseStreamResponse(response);
        } else {
            const response = await api.post<{ events: Event[] }>('/ai/ask', { question });
            return validateEvents(response.events);
        }
    } catch (error) {
        console.error('AI Q&A request failed:', error);
        return [{
            event: 'ERROR',
            data: {
                message: (error as Error).message || 'Request failed, please try again',
                time: Date.now(),
                errorType: (error as Error).name || 'NetworkError',
            }
        }];
    }
}

/**
 * 解析流式响应
 *
 * @param response - fetch响应对象
 */
async function parseStreamResponse(response: Response): Promise<Event[]> {
    const reader = response.body?.getReader();
    if (!reader) throw new Error('Unable to read stream response');

    const textDecoder = new TextDecoder();
    const events: Event[] = [];

    try {
        while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            // 处理流数据块
            const chunk = textDecoder.decode(value);
            const dataLines = chunk.split('\n').filter(line => line.startsWith('data: '));

            dataLines.forEach(line => {
                try {
                    const dataString = line.replace('data: ', '');
                    const eventData = JSON.parse(dataString) as Event;
                    events.push(eventData);
                } catch (e) {
                    console.error('Stream data parsing error:', e);
                }
            });
        }

        return validateEvents(events);
    } catch (e) {
        console.error('Stream processing error:', e);
        throw new Error('Stream processing interrupted: ' + (e as Error).message);
    }
}

// 验证事件数据结构
function validateEvents(events: Event[]): Event[] {
    // 验证逻辑（如前所示代码）
    return events;
}

// 定义AI API资源
export const aiApi = {
    search: createSearchApi(),
    conversation: createConversationApi(),
};

// 创建搜索API资源
function createSearchApi() {
    const resourcePath = '/ai/search';

    return {
        query: (searchTerm: string, options: Record<string, unknown>) => {
            const accessToken = envStorage.getItem('accessToken');
            if (!accessToken) {
                return Promise.reject(new Error('No access token found'));
            }
            return api.post(`${resourcePath}/query`, { searchTerm }, options);
        },
        getResults: (searchId: string) => {
            const accessToken = envStorage.getItem('accessToken');
            if (!accessToken) {
                return Promise.reject(new Error('No access token found'));
            }
            return api.get(`${resourcePath}/results/${searchId}`);
        },
    };
}

// 创建对话API资源
function createConversationApi() {
    const conversation = '/v1/chat/conversation';
    const conversationPoll = '/v1/chat/conversation/poll';
    const chatTopic = '/v1/chat/topic'
    const knowLedge = '/v1/chat/conversation/file'
    const markdown = '/v1/knowledge/file/markdown/content'
    const TopicShare = '/v1/chat/topic/share'
    const models = '/v1/chat/models'
    const proposal = '/test/proposal'
    const proposalDownload = '/test/proposal/download'

    // 检查访问令牌的辅助函数
    const checkAccessToken = () => {
        const accessToken = envStorage.getItem('accessToken');
        if (!accessToken) {
            throw new Error('No access token found');
        }
    };

    return {
        create: (initialQuestion: ConversationParams) => {
            checkAccessToken();
            initialQuestion.model = initialQuestion.model || useCounterStore.getState().modal
            return api.post(conversation, initialQuestion, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                }
            })
        },
        getPoll: (conversationNo: string) => {
            checkAccessToken();
            return api.get(`${conversationPoll}?`, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                params: {
                    conversationNo: conversationNo
                }
            });
        },
        getHistory: (params: AIResponseEvent) => {
            checkAccessToken();
            return api.get(`${chatTopic}`, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                params: {
                    ...params
                }
            });
        },
        getTopic: (topicNo: string | number) => {
            checkAccessToken();
            return api.get(`${chatTopic}/${topicNo}`, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                }
            });
        },
        getTitle: (topicNo: string | number) => {
            checkAccessToken();
            return api.get(`/v1/chat/topic/name`, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                params: {
                    topicNo: topicNo
                }
            });
        },
        postCancel: (conversationNo: string) => {
            checkAccessToken();
            return api.post(`/v1/chat/conversation/cancel`, {
                conversationNo: conversationNo
            }, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            });
        },
        getKnowledge: (conversationNo: string | number) => {
            checkAccessToken();
            return api.get(`${knowLedge}`, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                params: {
                    conversationNo: conversationNo
                }
            });
        },
        getMarkdownFile: (fileId: string | number) => {
            checkAccessToken();
            return api.get(`${markdown}`, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                params: {
                    fileId: fileId
                }
            });
        },
        getShare: (params: AIShareEvent) => {
            checkAccessToken();
            return api.get(`${TopicShare}`, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                params: {
                    ...params
                }
            });
        },
        getShareLink: (token: string) => {
            checkAccessToken();
            return api.get(`/v1/chat/topic/share/content?token=${token}`, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            });
        },
        getFeedback: (params: GetGoodFeedbackParams) => {
            checkAccessToken();
            return api.post(`${conversation}/feedback`, params, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });
        },
        getDelete: (params: string[]) => {
            checkAccessToken();
            return api.post(`${chatTopic}/delete`, {topicNos: params.join(',')}, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                }
            });
        },
        getModal: () => {
            checkAccessToken();
            return api.get(`${models}`, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                }
            });
        },
        // /api/test/proposal
        getProposal: (proposalId: string) => {
            checkAccessToken();
            return api.get(`${proposal}?proposalId=${proposalId}`, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                }
            });
        },
        // /api/test/proposal/download
        getProposalDownload: (proposalId: string) => {
            checkAccessToken();
            return getFileStream(`${proposalDownload}?proposalId=${proposalId}`);
        },

    };
}

/**
 * 用户配置API
 */
export const userApi = {
    /**
     * 获取用户信息
     */
    getUserInfo: () => {
        return api.get('/v1/user/info');
    },
    
    /**
     * 更新用户配置信息
     * @param params - 用户配置参数，只传需要更新的字段
     */
    updateProfile: (params: { nickName?: string; lang?: string }) => {
        const accessToken = envStorage.getItem('accessToken');
        if (!accessToken) {
            return Promise.reject(new Error('No access token found'));
        }
        
        // 只包含非空值的参数
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined && value !== '')
        );
        
        return api.post('/v1/user/profile', filteredParams, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            }
        });
    }
};
